import React, { useEffect, useState, useRef } from "react";
import styles from "../../pages/email/inbox/SLA_Timer.module.scss";
import { TODAY_BUSINESS_HOURS } from "../../constants";
interface SLAInfo {
  escalation_level: string;
  sla_started_at: string;
  sla_scheduled_end_at: string | null;
  sla_actual_breach_at: string | null;
  sla_duration_ms: number;
  is_resolved: boolean;
  resolved_at: string | null;
  resolution_method: string | null;
  first_response_at: string | null;
  status_at_breach: string;
  sla_emails: string[];
  is_non_actionable?: boolean;
  non_actionable_marked_at?: string | null;
  sla_type?: string | null;
}

interface SLMetricsTicketTimerProps {
  tagBasedSlBreach: SLAInfo[] | null;
  statusType?: string;
  otherMailDetails?: any;
}

const formatTime = (milliseconds: number): string => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const seconds = totalSeconds % 60;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const minutes = totalMinutes % 60;
  const totalHours = Math.floor(totalMinutes / 60);
  const hours = totalHours % 24;
  const days = Math.floor(totalHours / 24);

  if (milliseconds <= 60_000) {
    return `${String(minutes).padStart(2, "0")}m:${String(seconds).padStart(
      2,
      "0",
    )}s`;
  }
  if (days > 0) return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  return `${minutes}m ${seconds}s`;
};

const formatResolvedTime = (dateString: string | null): string => {
  if (!dateString) return "";
  return new Date(dateString)
    .toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    })
    .replace(/\//g, "/");
};

const getSlaTypeLabel = (raw: string | undefined): string => {
  if (!raw) return "Tag based sl:";
  const key = raw.trim().toLowerCase();
  if (key === "tag-sla") return "Tag SL:";
  if (key === "disposition-sla") return "Disposition SL:";
  if (key === "workflow-sla") return "Workflow SL:";
  const trimmed = raw.trim();
  return trimmed.endsWith(":") ? trimmed : `${trimmed}:`;
};

const isScheduledEndBreached = (scheduledEndAt: string | null): boolean => {
  if (!scheduledEndAt) return false;
  return new Date(scheduledEndAt).getTime() < Date.now();
};

const getFirstResponseTime = (otherMailDetails: any): string | null => {
  if (!otherMailDetails) return null;

  const emails =
    otherMailDetails?.action?.thread?.[0]?.emails ?? otherMailDetails?.emails;

  if (!Array.isArray(emails)) return null;

  const sentEmail = emails.find(
    (email: any) => email.is_received_email === false,
  );
  return sentEmail?.mail_sent_or_received_on ?? null;
};

const getBusinessHoursStatus = (): boolean => {
  const raw = localStorage.getItem(TODAY_BUSINESS_HOURS);
  if (!raw) return false;

  try {
    const bh = JSON.parse(raw);
    const now = new Date();
    const currentDay = now
      .toLocaleDateString("en-US", { weekday: "short" })
      .slice(0, 2);
    const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();
    const startTime = (bh.start_hours ?? 0) * 60 + (bh.start_minutes ?? 0);
    const endTime = (bh.end_hours ?? 24) * 60 + (bh.end_minutes ?? 0);

    return (
      !bh.is_holiday &&
      bh.day_of_week?.includes(currentDay) &&
      currentTimeInMinutes >= startTime &&
      currentTimeInMinutes <= endTime
    );
  } catch {
    return false;
  }
};

/**
 * API can leave older rows `is_resolved: false` while the journey ends on a later resolved row.
 * Prefer the last row when it is resolved; otherwise the last unresolved row (active escalation).
 */
const getCurrentSlaLog = (logs: SLAInfo[] | null | undefined): SLAInfo | undefined => {
  if (!logs?.length) return undefined;
  const last = logs[logs.length - 1];
  if (last?.is_resolved) return last;
  for (let i = logs.length - 1; i >= 0; i--) {
    if (!logs[i].is_resolved) return logs[i];
  }
  return last;
};

const SLMetricsTicketTimer: React.FC<SLMetricsTicketTimerProps> = ({
  tagBasedSlBreach,
  statusType,
  otherMailDetails,
}) => {
  const [timeLeft, setTimeLeft] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isBusinessHours, setIsBusinessHours] = useState(false);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const elapsedIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeLeftRef = useRef<number | null>(null);

  useEffect(() => {
    setIsBusinessHours(getBusinessHoursStatus());
    const id = setInterval(
      () => setIsBusinessHours(getBusinessHoursStatus()),
      60_000,
    );
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!tagBasedSlBreach?.length) return;

    const targetLog = getCurrentSlaLog(tagBasedSlBreach);
    if (!targetLog) return;

    if (targetLog.is_resolved) {
      timeLeftRef.current = 0;
      setTimeLeft(0);
      return;
    }

    if (!targetLog?.sla_scheduled_end_at) return;

    const scheduledEnd = new Date(targetLog.sla_scheduled_end_at).getTime();

    const syncTimeLeft = () => {
      const diff = scheduledEnd - Date.now();
      timeLeftRef.current = Math.max(diff, 0);
      setTimeLeft(timeLeftRef.current);
    };

    syncTimeLeft();

    intervalRef.current = setInterval(() => {
      if (timeLeftRef.current !== null) {
        timeLeftRef.current -= 1000;
        setTimeLeft(Math.max(timeLeftRef.current, 0));
      }
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [tagBasedSlBreach]);
  console.log("tagBasedSlBreach", tagBasedSlBreach);

  useEffect(() => {
    if (!tagBasedSlBreach?.length) return;

    const targetLog = getCurrentSlaLog(tagBasedSlBreach);
    if (!targetLog?.sla_scheduled_end_at || targetLog.is_resolved) return;

    const scheduledEnd = new Date(targetLog.sla_scheduled_end_at).getTime();

    const id = setInterval(() => {
      if (Math.abs(Date.now() - scheduledEnd) <= 1000) {
        clearInterval(id);
        setTimeout(() => window.location.reload(), 2000);
      }
    }, 1000);

    return () => clearInterval(id);
  }, [tagBasedSlBreach]);

  useEffect(() => {
    if (!tagBasedSlBreach?.length) return;

    const targetLog = getCurrentSlaLog(tagBasedSlBreach);
    if (!targetLog) return;

    setElapsedTime(0);

    if (targetLog.is_resolved) return;

    const breachTime = targetLog.sla_actual_breach_at
      ? new Date(targetLog.sla_actual_breach_at).getTime()
      : targetLog.sla_scheduled_end_at
      ? new Date(targetLog.sla_scheduled_end_at).getTime()
      : null;

    if (!breachTime || Date.now() < breachTime) return;

    const calculateElapsed = () =>
      setElapsedTime(Math.max(Date.now() - breachTime, 0));

    calculateElapsed();
    elapsedIntervalRef.current = setInterval(calculateElapsed, 1000);

    return () => {
      if (elapsedIntervalRef.current) clearInterval(elapsedIntervalRef.current);
    };
  }, [tagBasedSlBreach]);

  if (!tagBasedSlBreach?.length) return null;

  const currentLog = getCurrentSlaLog(tagBasedSlBreach);
  if (!currentLog) return null;

  /** Only the active row matters: L1 resolved + L2 active must still show L2 countdown, not L1 "Resolved". */
  const isCurrentLevelResolved = !!currentLog?.is_resolved;
  const isBreached =
    currentLog?.sla_actual_breach_at !== null ||
    isScheduledEndBreached(currentLog?.sla_scheduled_end_at ?? null);
  const isBlinking =
    timeLeft <= 60_000 && timeLeft > 0 && !isCurrentLevelResolved;

  const firstResponseTime = getFirstResponseTime(otherMailDetails);
  const slaTypeLabel = getSlaTypeLabel(currentLog?.sla_type ?? undefined);

  const getStatusInfo = (): { text: string; color: string } => {
    if (isCurrentLevelResolved && currentLog?.resolved_at) {
      return {
        text: `Resolved at ${formatResolvedTime(currentLog.resolved_at)}`,
        color: "#50cd89",
      };
    }

    if (currentLog?.is_non_actionable) {
      const at = currentLog.non_actionable_marked_at
        ? ` at ${formatResolvedTime(currentLog.non_actionable_marked_at)}`
        : "";
      return {
        text: `Marked as non-actionable${at}`,
        color: "#d97706",
      };
    }

    if (isBreached && firstResponseTime) {
      return {
        text: `First Response at ${formatResolvedTime(firstResponseTime)}`,
        color: "#17a2b8",
      };
    }

    if (!isBusinessHours) {
      return { text: "Off Hours", color: "orange" };
    }

    if (isBreached) {
      return { text: `SL Breached + ${formatTime(elapsedTime)}`, color: "red" };
    }

    if (timeLeft > 0) {
      return { text: formatTime(timeLeft), color: "green" };
    }

    return { text: `SL Breached + ${formatTime(elapsedTime)}`, color: "red" };
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="d-flex align-items-center justify-content-between">
      <div className="d-flex align-items-center">
        <span className="me-3 fs-5 fw-semibold">{slaTypeLabel}</span>
        <div
          className={`card-text fs-5 fw-bold ${
            isBlinking ? styles.blinking : ""
          }`}
          style={{
            color:
              !isBlinking || isCurrentLevelResolved
                ? statusInfo.color
                : undefined,
          }}
        >
          {statusInfo.text}
        </div>
      </div>
    </div>
  );
};

export default SLMetricsTicketTimer;